if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        // Auto-play videos when they come into view
        this.initAutoPlayObserver();

        if (this.elements.viewer) {
          const slider = this.elements.viewer.querySelector('[id^="Slider-"]') || this.elements.viewer;
          if (slider) {
            let scrollTimeout;
            slider.addEventListener('scroll', () => {
              clearTimeout(scrollTimeout);
              scrollTimeout = setTimeout(() => {
                this.checkScrollVisibleMedia(slider);
              }, 50);
            }, { passive: true });
          }

          this.elements.viewer.addEventListener(
            'slideChanged',
            (event) => {
              if (event.detail?.currentElement) {
                this.playActiveMedia(event.detail.currentElement);
              }
            }
          );
        }

        if (!this.elements.thumbnails) return;

        // Thumbnail click handler
        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((thumb) => {
            thumb.querySelector('button')?.addEventListener('click', (e) => {
              e.preventDefault();

              const mediaId = thumb.dataset.target;
              this.setActiveMedia(mediaId);
              this.selectVariantFromAltText(thumb);
            });
          });

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      initAutoPlayObserver() {
        if (!('IntersectionObserver' in window)) return;

        const slider = this.elements.viewer?.querySelector('[id^="Slider-"]');

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.playActiveMedia(entry.target);
              }
            });
          },
          {
            root: slider && slider.clientWidth > 0 ? slider : null,
            threshold: 0.3
          }
        );

        const viewportObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.playActiveMedia(entry.target);
              }
            });
          },
          { threshold: 0.3 }
        );

        this.querySelectorAll('.product__media-item').forEach((item) => {
          observer.observe(item);
          viewportObserver.observe(item);
        });
      }

      checkScrollVisibleMedia(slider) {
        if (!slider) return;
        const sliderRect = slider.getBoundingClientRect();
        const items = this.querySelectorAll('.product__media-item');

        items.forEach((item) => {
          const itemRect = item.getBoundingClientRect();
          const isVisible =
            itemRect.left < sliderRect.right - itemRect.width * 0.3 &&
            itemRect.right > sliderRect.left + itemRect.width * 0.3;

          if (isVisible) {
            const deferred = item.querySelector('.deferred-media');
            if (deferred) {
              this.playActiveMedia(item);
            }
          }
        });
      }

      // =========================
      // SLIDE EVENT SYNC
      // =========================
      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;

        const thumb = this.elements.thumbnails?.querySelector(
          `[data-target="${mediaId}"]`
        );

        if (thumb) this.setActiveThumbnail(thumb);
        this.playActiveMedia(event.detail.currentElement);
      }

      // =========================
      // MAIN MEDIA SWITCH (FIXED)
      // =========================
      setActiveMedia(mediaId) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );

        if (!activeMedia) return;

        // remove old active
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((el) => el.classList.remove('is-active'));

        activeMedia.classList.add('is-active');

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        // =========================
        // 🔥 FIXED SMOOTH SCROLL LOGIC
        // =========================
        const container =
          this.elements.viewer.querySelector('[id^="Slider-"]') ||
          this.elements.viewer;

        const offset =
          activeMedia.offsetLeft -
          (container.clientWidth / 2 - activeMedia.clientWidth / 2);

        container.scrollTo({
          left: offset,
          behavior: 'smooth'
        });

        // =========================
        // thumbnail sync
        // =========================
        const activeThumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${mediaId}"]`
        );

        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);

        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      // =========================
      // THUMBNAIL UI ONLY
      // =========================
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((btn) => btn.removeAttribute('aria-current'));

        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        const thumbContainer =
          this.elements.thumbnails.querySelector('[id^="Slider-"]') ||
          this.elements.thumbnails;

        if (thumbContainer && typeof thumbContainer.scrollTo === 'function') {
          const offset =
            thumbnail.offsetLeft -
            (thumbContainer.clientWidth / 2 - thumbnail.clientWidth / 2);

          thumbContainer.scrollTo({
            left: offset,
            behavior: 'smooth',
          });
        }
      }

      // =========================
      // ACCESSIBILITY
      // =========================
      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;
        window.pauseAllMedia?.();

        const deferred = activeItem.querySelector('.deferred-media');
        if (deferred) {
          deferred.loadContent(false);

          const video = deferred.querySelector('video');
          if (video) {
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                video.muted = true;
                video.play().catch(() => {});
              });
            }
          }

          const iframe = deferred.querySelector('iframe');
          if (iframe) {
            if (iframe.classList.contains('js-youtube')) {
              iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            } else if (iframe.classList.contains('js-vimeo')) {
              iframe.contentWindow?.postMessage('{"method":"play"}', '*');
            }
          }
        }
      }

      preventStickyHeader() {
        const header = document.querySelector('sticky-header');
        if (!header) return;

        header.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        if (!slider) return;

        slider.setAttribute('role', 'presentation');

        slider.querySelectorAll('[data-media-id]').forEach((el) => {
          el.setAttribute('role', 'presentation');
        });
      }

      selectVariantFromAltText(thumbElement) {
        selectVariantFromImage(thumbElement);
      }
    }
  );
}

// Global variant matching helper using Alt text & image filename (src)
function selectVariantFromImage(element) {
  if (!element) return;

  const btn = element.tagName === 'BUTTON' ? element : element.querySelector('button') || element;
  const img = element.querySelector('img') || (element.tagName === 'IMG' ? element : null);

  let altText = btn.getAttribute('product-alt') ||
                btn.getAttribute('data-media-alt') ||
                img?.getAttribute('alt') ||
                btn.getAttribute('aria-label') || '';

  altText = altText.replace(/^Load image \d+: /i, '').replace(/^Load video \d+: /i, '').trim();

  let imgSrc = btn.dataset.full ||
               img?.getAttribute('src') ||
               img?.getAttribute('srcset') ||
               '';

  const filename = imgSrc.split('/').pop().split('?')[0];

  const targetAltClean = altText.toLowerCase().replace(/[^a-z0-9]/g, '');
  const targetSrcClean = filename.toLowerCase().replace(/[^a-z0-9]/g, '');

  const variantContainers = document.querySelectorAll(
    'variant-selects, variant-radios, .product-form__input, .env-qty-options, form[action*="/cart/add"]'
  );

  variantContainers.forEach(function(container) {
    // 1. SELECT Dropdowns (e.g. Design dropdown)
    container.querySelectorAll('select').forEach(function(select) {
      let matchedOpt = null;

      Array.from(select.options).forEach(function(opt) {
        if (!opt.value) return;
        const optVal = opt.value.trim().toLowerCase();
        const optValClean = optVal.replace(/[^a-z0-9]/g, '');
        const optTextClean = opt.text.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

        if (!optValClean) return;

        const matchesSrc = targetSrcClean && targetSrcClean.includes(optValClean);
        const matchesAlt = targetAltClean && targetAltClean !== 'celebration' &&
                           (targetAltClean === optValClean || targetAltClean.includes(optValClean) || optValClean.includes(targetAltClean) || targetAltClean.includes(optTextClean));

        if (matchesSrc || matchesAlt) {
          matchedOpt = opt;
        }
      });

      if (matchedOpt && select.value !== matchedOpt.value) {
        Array.from(select.options).forEach(function(o) { o.removeAttribute('selected'); });
        select.value = matchedOpt.value;
        matchedOpt.setAttribute('selected', 'selected');
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // 2. Radio Inputs & Swatches & Pills
    container.querySelectorAll('input[type="radio"]').forEach(function(radio) {
      if (!radio.value) return;
      const val = radio.value.trim().toLowerCase();
      const valClean = val.replace(/[^a-z0-9]/g, '');

      if (!valClean) return;

      const matchesSrc = targetSrcClean && targetSrcClean.includes(valClean);
      const matchesAlt = targetAltClean && targetAltClean !== 'celebration' &&
                         (targetAltClean === valClean || targetAltClean.includes(valClean) || valClean.includes(targetAltClean));

      if (matchesSrc || matchesAlt) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));

          const card = document.querySelector('.env-qty-card[data-option-value="' + CSS.escape(radio.value) + '"]');
          if (card) {
            const cardContainer = card.closest('.env-qty-options');
            if (cardContainer) {
              cardContainer.querySelectorAll('.env-qty-card').forEach(function(c) { c.classList.remove('env-selected'); });
            }
            card.classList.add('env-selected');
          }
        }
      }
    });
  });
}

// Global listener for thumbnail clicks to select variant by image filename/alt text
document.addEventListener('click', function(e) {
  const thumb = e.target.closest('.thumbnail, [data-target], .custom-thumbnails .thumbnail, .thumbnail-list__item');
  if (!thumb) return;

  selectVariantFromImage(thumb);
});